import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Video, Calendar, Copy, Trash2, Play } from 'lucide-react';
import { useMyMeetings, useCreateInstant, useCancelMeeting } from '@/features/meetings/useMeetings';
import { useEnterMeeting } from '@/features/meetings/useEnterMeeting';
import type { MeetingStatus } from '@/features/meetings/meeting.api';

const STATUS_TABS: { label: string; value?: MeetingStatus }[] = [
  { label: 'All' },
  { label: 'Upcoming', value: 'SCHEDULED' },
  { label: 'Live', value: 'LIVE' },
  { label: 'Ended', value: 'ENDED' },
];

export default function MeetingsPage(): JSX.Element {
  const [status, setStatus] = useState<MeetingStatus | undefined>();
  const { data, isLoading } = useMyMeetings({ status });
  const createInstant = useCreateInstant();
  const cancel = useCancelMeeting();
  const enterMeeting = useEnterMeeting();

  const startInstant = async () => {
    const m = await createInstant.mutateAsync('Instant Meeting');
    await enterMeeting(m);
  };

  const copyInvite = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const meetings = data?.data.meetings ?? [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Meetings</h1>
        <div className="flex gap-2">
          <button className="btn-ghost border border-slate-300 dark:border-slate-700" onClick={startInstant}>
            <Video size={16} /> New meeting
          </button>
          <Link to="/schedule" className="btn-primary">
            <Calendar size={16} /> Schedule
          </Link>
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => setStatus(t.value)}
            className={`rounded-full px-4 py-1.5 text-sm ${
              status === t.value ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {!isLoading && meetings.length === 0 && (
          <div className="card text-sm text-slate-500">No meetings here yet.</div>
        )}
        {meetings.map((m) => (
          <div key={m.id} className="card flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{m.title}</h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{m.status}</span>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                Code: {m.meetingCode}
                {m.scheduledStart && ` · ${new Date(m.scheduledStart).toLocaleString()}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-ghost p-2" title="Copy invite" onClick={() => copyInvite(m.inviteUrl)}>
                <Copy size={16} />
              </button>
              <button onClick={() => enterMeeting(m)} className="btn-primary px-3 py-1.5 text-sm">
                <Play size={14} /> Join
              </button>
              {m.status !== 'ENDED' && m.status !== 'CANCELLED' && (
                <button
                  className="btn-ghost p-2 text-red-600"
                  title="Cancel"
                  onClick={() => cancel.mutate(m.id)}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
